import { Spinner } from "@npco/zeller-design-system";
import * as Illustration from "@npco/zeller-design-system/illustration";

import { PageTemplate } from "./page-template";

/**
 * The copy for the shared pages, kept apart from their rendering so the
 * `closeWith*` terminators can send the merchant the same words they read on
 * screen. See `createCloseHandlers`.
 */
export function createOperationCopy(operation: "Payment" | "Refund") {
	return {
		Cancelled: {
			title: `${operation} Cancelled`,
			// Not rendered — the cancelled page shows its title alone. Carried so
			// the host receives something beyond a restatement of the error type.
			message: `The ${operation.toLowerCase()} was cancelled before it completed.`,
		},
		ErrorDisconnected: {
			title: "Terminal Error",
			message:
				"The Zeller Terminal has lost connection with this POS register. Please check the connection and try again. If this is the first time you're setting up the register, you may need to set up the terminal connection first.",
		},
		ErrorInactivityTimeout: {
			title: "Cancelled due to inactivity",
			message: `${operation} has been cancelled due to inactivity. Please try again.`,
		},
		ErrorNetwork: {
			title: "Unable to connect to Zeller",
			message: "Please ensure this device is connected to the internet.",
		},
		ErrorOperation: {
			title: "Operation Failed",
			message: "This transaction could not be completed.",
		},
		ErrorTerminalBusy: {
			title: "Terminal Busy",
			message:
				"The Zeller Terminal is currently busy. Please check the terminal and try again.",
		},
		ErrorTerminalUnreachable: {
			title: "Failed to connect to Terminal",
			message:
				"Please check that your Zeller Terminal is connected to the Internet and try again.",
		},
		ErrorTerminalMissing: {
			title: "No paired terminal found",
			message: `This device is not connected to a Zeller Terminal. Please pair it to process a ${operation.toLowerCase()}.`,
		},
	};
}

/**
 * The pages every terminal operation shows, so purchase and both refund flows
 * declare them once. Pages belonging to a single operation — its outcome
 * screens, transaction lookup, tip entry — stay in that flow's own render.
 *
 * `operation` names the thing being performed, for the handful of pages whose
 * copy is otherwise identical.
 */
export function createOperationPages(operation: "Payment" | "Refund") {
	const copy = createOperationCopy(operation);
	return {
		Initialising: PageTemplate({
			image: <Spinner size="lg" />,
			title: "Connecting",
			intermediate: true,
		}),
		AwaitProcessing: PageTemplate({
			image: <Spinner size="lg" />,
			title: `Processing ${operation}`,
			intermediate: true,
		}),
		AwaitAccountSelection: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Waiting for Account selection",
			intermediate: true,
		}),
		AwaitApplicationSelection: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Waiting for Card Application selection",
			intermediate: true,
		}),
		AwaitPaymentMethod: PageTemplate({
			image: <Illustration.TerminalT2TapNow />,
			title: "Waiting for Card or Device to be presented",
			intermediate: true,
		}),
		AwaitPinEntry: PageTemplate({
			image: <Illustration.TerminalT2Pin />,
			title: "Waiting for Card PIN entry",
			intermediate: true,
		}),
		AwaitSignature: PageTemplate({
			image: <Illustration.TerminalT2Signature />,
			title: "Waiting for Signature entry",
			intermediate: true,
		}),
		DisplayPaymentMessage: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Card Payment Message pending",
			intermediate: true,
		}),
		Cancelled: PageTemplate({
			image: <Illustration.SuccessThumbsUp />,
			title: copy.Cancelled.title,
		}),
		ErrorDisconnected: PageTemplate({
			image: <Illustration.Error />,
			...copy.ErrorDisconnected,
		}),
		ErrorInactivityTimeout: PageTemplate({
			image: <Illustration.ErrorBoatSinking loop={false} />,
			...copy.ErrorInactivityTimeout,
		}),
		ErrorNetwork: PageTemplate({
			image: <Illustration.ErrorBoatSinking loop={false} />,
			...copy.ErrorNetwork,
			steps: [
				"Confirm your device is connected to your network.",
				"Check if the network is connected to the internet.",
			],
		}),
		ErrorOperation: PageTemplate({
			image: <Illustration.Error />,
			...copy.ErrorOperation,
		}),
		ErrorTerminalBusy: PageTemplate({
			image: <Illustration.ErrorDominoes loop={false} />,
			...copy.ErrorTerminalBusy,
		}),
		ErrorTerminalUnreachable: PageTemplate({
			image: <Illustration.ErrorDominoes loop={false} />,
			...copy.ErrorTerminalUnreachable,
		}),
		ErrorTerminalMissing: PageTemplate({
			image: <Illustration.ErrorQuestionMark />,
			...copy.ErrorTerminalMissing,
		}),
	};
}
